import {Component, computed, effect, inject, signal, viewChild} from "@angular/core";
import {NgTemplateOutlet} from "@angular/common";
import {Menu} from "primeng/menu";
import {Button} from "primeng/button";
import {MenuItem} from "primeng/api";
import {UnitsEnum} from "@shared/enum";
import {DatasetStore, ElementTypes, WidgetElement} from "@entities/widget-element";
import {WidgetConstructorStore} from "../../store/widget-constructor-store";
import {WidgetConstructorMapperService} from "../../lib/widget-constructor.mapper.service";
import {DeviceMetricNode, DeviceNode, Metric} from "../../model/constructor-menu-nodes";
import {maxDatasets} from "../../enum/max-datasets";

@Component({
    selector: "constructor-node-select",
    imports: [
        Menu,
        Button,
        NgTemplateOutlet,

    ],
    templateUrl: "./node-select.component.html",
    styleUrl: "./node-select.component.scss"
})
export class NodeSelectComponent {
    readonly nodesDevicesMenu = viewChild<Menu>("nodesDevicesMenu");
    readonly nodesMetricsMenu = viewChild<Menu>("nodesMetricsMenu");

    readonly constructorStore = inject(WidgetConstructorStore);
    readonly datasetStore = inject(DatasetStore);
    readonly mapper = inject(WidgetConstructorMapperService);

    readonly settingsLoading = computed(() => this.constructorStore.datasetSettings()?.isLoading());
    readonly canAddDataset = computed(() => {
        if (this.element()) {
            return this.element()!.data.content.datasets.length < maxDatasets[this.element()!.data.content.elementType];
        } else return null;
    });
    readonly selectedElementIncludesLastSelectedDevice = computed(
        () => this.element()?.data.content.datasets.map(dataset => dataset.id).includes(this.lastSelectedDeviceNode()?.id || "")
    );
    readonly selectedElementIncludesSelectedMetric = computed(
        () => this.element()?.data.content.datasets.map(dataset => dataset.id).includes(this.selectedMetric()?.id || "")
    );
    readonly editingElementDatasetsIds = computed(
        () => this.constructorStore.editingElement()?.data.content.datasets.map(dataset => dataset.id) || [],
    );
    readonly lastSelectedDeviceNodeChildren = computed(
        () => this.lastSelectedDeviceNode()?.nodes.map(node => this.constructorStore.deviceNodes()[node.id])
    );
    readonly lastSelectedMetricsNodeChildren = computed(
        () => this.lastSelectedMetricsNode()?.nodes.map(node => this.constructorStore.deviceMetricNodes()[node.id])
    );
    readonly lastSelectedDeviceNode = computed(() => this.selectedDeviceNodes().at(-1));
    readonly lastSelectedMetricsNode = computed(() => this.selectedMetricsNodes().at(-1));
    readonly selectedDeviceNodes = computed<Array<DeviceNode>>(() => this.constructorStore.selectedDeviceNodes());
    readonly selectedMetricsNodes = computed<Array<DeviceMetricNode>>(() => this.constructorStore.selectedMetricsNodes());
    readonly selectedMetric = computed<Metric | null>(() => this.element() ? this.constructorStore.selectedMetric() : null);
    readonly needMetrics = computed(() => this.elementNeedMetrics(this.element()));
    readonly element = computed(() => this.constructorStore.editingElement());
    readonly fetching = computed(() => this.constructorStore.isFetching());

    readonly selectedDeviceNodeIndex = signal(0);
    readonly selectedMetricsNodeIndex = signal(0);

    readonly changeDevicesMenuNodes = computed(() => this.mapper.mapDeviceNodesToMenuItems(
        this.constructorStore.changeDevicesNodes(),
        this.changeDevicesMenuNodeCommand,
        this.constructorStore.disabledDeviceNodeId()
    ));

    readonly changeMetricsMenuNodes = computed(() => this.mapper.concatMenuItems(
        this.mapper.mapMetricNodesToMenuItem(
            this.constructorStore.changeMetricsNodesDevices(),
            this.changeMetricsMenuNodeCommand,
            [this.constructorStore.disabledDeviceMetricsNodeId(), ...this.editingElementDatasetsIds()]
        ),
        this.mapper.mapMetricsToMenuItem(
            this.constructorStore.changeMetricsNodesMetrics(),
            metric => this.metricCommand(metric, true),
            [this.selectedMetric()?.id || null, ...this.editingElementDatasetsIds()]
        ),
        this.mapper.mapAnalyticalMetricsToMenuItem(
            this.constructorStore.changeMetricsNodesAnalyticalMetrics(),
            metric => this.metricCommand(metric, true),
            [this.selectedMetric()?.id || null, ...this.editingElementDatasetsIds()]
        )
    ));

    readonly changeMetricsMenuItems = computed(() => this.mapper.concatMenuItems(
        this.mapper.mapMetricNodesToMenuItem(
            this.constructorStore.changeMetricsNodesDevices(),
            this.changeMetricsMenuNodeCommand,
            []
        ),
        this.mapper.mapMetricsToMenuItem(
            this.constructorStore.changeMetricsNodesMetrics(),
            metric => this.metricCommand(metric, false),
            [this.selectedMetric()?.id || null, ...this.editingElementDatasetsIds()]
        ),
        this.mapper.mapAnalyticalMetricsToMenuItem(
            this.constructorStore.changeMetricsNodesAnalyticalMetrics(),
            metric => this.metricCommand(metric, false),
            [this.selectedMetric()?.id || null, ...this.editingElementDatasetsIds()]
        )
    ));

    readonly devicesMenuNodes = computed<MenuItem[]>(() => {
        if (this.lastSelectedDeviceNode()) {
            return this.mapper.mapDeviceNodesToMenuItems(
                this.lastSelectedDeviceNodeChildren()!,
                this.defaultDeviceNodeCommand,
            );
        } else {
            return this.stationDevicesNodes();
        }
    });

    readonly metricsMenuNodes = computed<MenuItem[]>(() => {
        if (this.lastSelectedMetricsNode()) {
            return this.mapper.concatMenuItems(
                this.mapper.mapMetricNodesToMenuItem(
                    this.lastSelectedMetricsNodeChildren()!,
                    this.defaultMetricsNodeCommand,
                    []
                ),
                this.mapper.mapMetricsToMenuItem(
                    this.lastSelectedMetricsNode()!.metrics,
                    metric => this.metricCommand(metric, false),
                    [this.selectedMetric()?.id || null, ...this.editingElementDatasetsIds()]
                ),
                this.mapper.mapAnalyticalMetricsToMenuItem(
                    this.lastSelectedMetricsNode()!.analyticalMetrics,
                    metric => this.metricCommand(metric, false),
                    [this.selectedMetric()?.id || null, ...this.editingElementDatasetsIds()]
                )
            );
        } else {
            return this.stationMetricsNodes();
        }
    });

    readonly stationDevicesNodes = computed<MenuItem[]>(() => {
        return this.mapper.mapDeviceNodesToMenuItems(
            this.constructorStore.stationDevicesNodes(),
            node => this.defaultDeviceNodeCommand(node, true),
        );
    });

    readonly stationMetricsNodes = computed<MenuItem[]>(() => {
        return this.mapper.mapDeviceNodesToMenuItems(
            this.constructorStore.stationMetricsNodes(),
            node => this.defaultMetricsNodeCommand(node as DeviceMetricNode, true),
        );
    });

    private readonly loadStationNodes = effect(() => {
        if (this.element()) {
            if (this.elementNeedMetrics(this.element())) {
                this.loadStationMetricNodes();
            } else {
                this.loadStationDeviceNodes();
            }
        }
    });

    removeDeviceNode() {
        const index = this.selectedDeviceNodeIndex();
        this.constructorStore.replaceSelectedDeviceNodesFrom(index);
        this.removeMetricNode();
    }

    removeDeviceMetricNode() {
        const index = this.selectedMetricsNodeIndex();
        this.constructorStore.replaceSelectedMetricsNodesFrom(index);
        this.removeMetricNode();
    }

    removeMetricNode() {
        this.constructorStore.resetSelectedMetric();
    }

    updateEditingElementDevices() {
        const datasets = this.constructorStore.editingElement()!.data.content.datasets;
        const device = this.lastSelectedDeviceNode()!;
        const dataset: Metric = {
            id: device.id,
            name: device.name,
            unit: "" as keyof typeof UnitsEnum
        };

        this.constructorStore.updateEditingElementContent({datasets: [...datasets, dataset]});
        this.loadDataset(dataset.id);
    }

    updateEditingElementMetrics() {
        const datasets = this.constructorStore.editingElement()!.data.content.datasets;
        const lastDevice = this.lastSelectedMetricsNode()!;
        const metric = this.selectedMetric()!;
        const dataset: Metric = {
            id: metric.id,
            name: lastDevice.name + "/" + metric.name,
            unit: metric.unit
        };

        this.constructorStore.updateEditingElementContent({datasets: [...datasets, dataset]});
        this.loadDataset(this.selectedMetric()!.id);
    }

    setSelectedDeviceNodeId(id: string | null) {
        this.constructorStore.setSelectedDeviceNodeId(id);
    }

    setSelectedMetricsNodeId(id: string | null) {
        this.constructorStore.setSelectedMetricsNodeId(id);
    }

    setDisabledDeviceNodeId(id: string) {
        this.constructorStore.setDisabledDeviceNodeId(id);
    }

    setDisabledDeviceMetricsNodeId(id: string) {
        this.constructorStore.setDisabledDeviceMetricsNodeId(id);
    }

    toggleDevicesMenu(event: Event) {
        this.nodesDevicesMenu()?.toggle(event);
    }

    toggleMetricsMenu(event: Event) {
        this.nodesMetricsMenu()?.toggle(event);
    }

    private defaultDeviceNodeCommand = (node: DeviceNode, loadNodes = false) => {
        this.constructorStore.addSelectedDeviceNode(node);
        if (loadNodes) {
            this.loadDeviceNodes();
        }
    };

    private defaultMetricsNodeCommand = (node: DeviceMetricNode, loadNodes = false) => {
        this.constructorStore.addSelectedMetricsNode(node);
        if (loadNodes) {
            this.loadDeviceMetricNodes();
        }
    };

    private changeDevicesMenuNodeCommand = (node: DeviceNode) => {
        this.constructorStore.replaceSelectedDeviceNodesFrom(this.selectedDeviceNodeIndex(), node);
        if (!this.selectedDeviceNodeIndex()) {
            this.loadDeviceNodes();
        }
    };

    private changeMetricsMenuNodeCommand = (node: DeviceMetricNode) => {
        this.constructorStore.replaceSelectedMetricsNodesFrom(this.selectedMetricsNodeIndex(), node);
        this.constructorStore.resetSelectedMetric();
        if (!this.selectedMetricsNodeIndex()) {
            this.loadDeviceMetricNodes();
        }
    };

    private metricCommand = (metric: Metric, replace: boolean) => {
        this.constructorStore.setSelectedMetric(metric);
        if (replace) {
            this.constructorStore.replaceSelectedMetricsNodesFrom(this.selectedMetricsNodeIndex());
        }
    };

    private loadStationDeviceNodes() {
        this.constructorStore.loadStationDeviceNodes();
    }

    private loadStationMetricNodes() {
        this.constructorStore.loadStationMetricNodes();
    }

    private loadDeviceNodes() {
        const id = this.lastSelectedDeviceNode()!.id;
        this.constructorStore.loadDeviceNodes(id);
    }

    private loadDeviceMetricNodes() {
        const id = this.lastSelectedMetricsNode()!.id;
        this.constructorStore.loadDeviceMetricNodes(id);
    }

    private loadDataset(id: string) {
        this.datasetStore.loadDataset({
            elementId: this.element()!.id,
            datasetId: id,
            elementType: this.element()!.data.content.elementType,
            datasetType: this.element()!.data.metadata.datasetType,
            temporalCount: this.element()!.data.metadata.temporalCount,
            precision: this.element()!.data.metadata.precision,
        });
    }

    private elementNeedMetrics(element: WidgetElement | null) {
        return element?.data.content.elementType !== ElementTypes.DEVICE_STATE;
    }
}
